import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, Plus, X, Loader2, ShieldAlert } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

import { useCare, errorText } from '../../care/CareContext';
import { keyed } from '../../lib/listKeys';

const G6PD = ['Unknown', 'Normal', 'Deficient'];
const BLOOD = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const EMPTY = { g6pd_status: 'Unknown', blood_group: '', allergies: [], conditions: [], diagnoses: [], current_medications: [], immunizations: [], notes: '' };

function ChipList({ label, values, onChange, testId }) {
  const [draft, setDraft] = useState('');
  const add = () => { const v = draft.trim(); if (!v) return; onChange([...values, v]); setDraft(''); };
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-1.5">
        {keyed(values).map(({ item: v, key }, i) => (
          <span key={key} className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 rounded-full px-2.5 py-1 text-sm">
            {v}
            <button type="button" onClick={() => onChange(values.filter((_, j) => j !== i))} className="hover:text-red-600">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)}
          data-testid={testId}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Type and press Enter" />
        <Button type="button" onClick={add} variant="outline"><Plus className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

export default function PortalClinicalSharePage() {
  const { api: care, branch } = useCare();
  const [profile, setProfile] = useState(null);
  const [shared, setShared] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let live = true;
    if (!branch) { setProfile({ ...EMPTY }); return; }
    setLoadError('');
    care.get('/clinical-profile', { params: { tenant_id: branch } }).then((r) => {
      const row = r.data.profile;
      if (!live) return;
      setShared(!!row);
      setProfile({ ...EMPTY, ...(row?.profile || {}) });
    }).catch((e) => { if (live) setLoadError(errorText(e)); });
    return () => { live = false; };
  }, [branch, care]);

  if (loadError) return <p role="alert" data-testid="portal-clinical-error">{loadError}</p>;
  if (!branch) return <p className="care-empty" data-testid="portal-clinical-no-branch">Choose a service branch first.</p>;
  if (!profile) return <div className="py-20 text-center text-slate-400" data-testid="portal-clinical-loading"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  const save = async () => {
    setSaving(true);
    try {
      await care.put('/clinical-profile', { tenant_id: branch, ...profile });
      setShared(true);
      toast.success('Clinical profile shared with your pharmacy');
    } catch (e) { toast.error(errorText(e)); } finally { setSaving(false); }
  };

  const revoke = async () => {
    try {
      await care.post('/clinical-profile/revoke', { tenant_id: branch });
      setShared(false);
      toast.success('Sharing stopped for this branch');
    } catch (e) { toast.error(errorText(e)); }
  };

  return (
    <div className="space-y-5" data-testid="portal-clinical-page">
      <h1 className="text-2xl font-bold text-slate-900">Share Clinical Info</h1>
      <p className="text-xs text-muted-foreground flex items-start gap-2" data-testid="portal-clinical-notice">
        <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
        Opt-in only. This shares allergies, G6PD status, conditions and medicines with your chosen branch's
        pharmacist team — separate from your private self-profile. Not a diagnosis, emergency service or
        automatic medication recommendation.
      </p>
      {shared && <p className="care-legacy" data-testid="portal-clinical-shared-notice">Currently shared with this branch's pharmacist team.</p>}

      <Card>
        <CardHeader><CardTitle>Key clinical details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>G6PD status</Label>
            <Select value={profile.g6pd_status || 'Unknown'} onValueChange={(v) => setProfile({ ...profile, g6pd_status: v })}>
              <SelectTrigger data-testid="portal-clinical-g6pd"><SelectValue /></SelectTrigger>
              <SelectContent>{G6PD.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Blood group</Label>
            <Select value={profile.blood_group || ''} onValueChange={(v) => setProfile({ ...profile, blood_group: v })}>
              <SelectTrigger data-testid="portal-clinical-blood"><SelectValue placeholder="Unknown" /></SelectTrigger>
              <SelectContent>{BLOOD.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Clinical history</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <ChipList label="Allergies" values={profile.allergies || []} onChange={(v) => setProfile({ ...profile, allergies: v })} testId="portal-clinical-allergy-input" />
          <ChipList label="Pre-existing conditions" values={profile.conditions || []} onChange={(v) => setProfile({ ...profile, conditions: v })} testId="portal-clinical-condition-input" />
          <ChipList label="Major diagnoses" values={profile.diagnoses || []} onChange={(v) => setProfile({ ...profile, diagnoses: v })} testId="portal-clinical-diagnosis-input" />
          <ChipList label="Current medications" values={profile.current_medications || []} onChange={(v) => setProfile({ ...profile, current_medications: v })} testId="portal-clinical-med-input" />
          <ChipList label="Immunizations" values={profile.immunizations || []} onChange={(v) => setProfile({ ...profile, immunizations: v })} testId="portal-clinical-immunization-input" />
          <div>
            <Label>Notes for your pharmacist</Label>
            <Textarea rows={3} value={profile.notes || ''} onChange={(e) => setProfile({ ...profile, notes: e.target.value })} data-testid="portal-clinical-notes" />
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-3 flex-wrap">
        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="portal-clinical-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          Share with pharmacy
        </Button>
        {shared && <Button variant="outline" onClick={revoke} data-testid="portal-clinical-revoke">Stop sharing</Button>}
      </div>
    </div>
  );
}
